import {Component} from "react";
import PropTypes from "prop-types";
import "./MyAccordion.css";

const baseClassName = 'MyAccordion';

const MyBullet = class extends Component {
	render() {
		return (
			<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32" fill="currentColor" stroke="none" style={this.props.style||{}}>
				<path d="M24.8175,16.86432,9.503,25.77667A1,1,0,0,1,8,24.91235V7.08765a1,1,0,0,1,1.503-.86432L24.8175,15.13568A1.00006,1.00006,0,0,1,24.8175,16.86432Z"/>
			</svg>
		);
	}
}

/**
 * @private
 * @typedef MyAccordionProps
 * @type {Object}
 * @property {JSX.Element|React.node|string} title The title to be displayed in accordion header
 * @property {JSX.Element|React.node|string} [bullet] The bullet of the accordion toggler
 * @property {boolean} [open] Initial status, defaults to false (closed)
 * @property {React.CSSProperties} [headerStyle] Optional additional style properties for header
 * @property {MyAccordionKnownTogglerFunction} [onBeforeOpen] invoked before opening the accordion
 * @property {MyAccordionKnownTogglerFunction} [onBeforeClose] invoked before closing the accordion
 * @property {MyAccordionKnownTogglerFunction} [onOpen] invoked after opening the accordion
 * @property {MyAccordionKnownTogglerFunction} [onClose] invoked after closing the accordion
 * @property {MyAccordionTogglerFunction} [onToggle] invoked after opening/closing the accordion
 * @property {JSX.Element|React.node} [children] The content of accordion body
 */

/**
 * @private
 * @typedef MyAccordionState
 * @type {Object}
 * @property {boolean} open Is the accordion open?
 */

/**
 * @desc A replacement for default Bootstrap accordion, a single accordion but with many more options
 * @desc Used to hide/display a section, given its title
 * @desc Put content as children of the node.
 * @version 1.1
 * @author [Marco Balestra](https://altersoftware.it)
 * @type {Component}
 * @property {JSX.Element|React.node|string} title The title to be displayed in accordion header
 * @property {JSX.Element|React.node|string} [bullet] The bullet of the accordion toggler
 * @property {boolean} [open] Initial status, defaults to false (closed)
 * @property {React.CSSProperties} [headerStyle] Optional additional style properties for header
 * @property {MyAccordionKnownTogglerFunction} [onBeforeOpen] invoked before opening the accordion
 * @property {MyAccordionKnownTogglerFunction} [onBeforeClose] invoked before closing the accordion
 * @property {MyAccordionKnownTogglerFunction} [onOpen] invoked after opening the accordion
 * @property {MyAccordionKnownTogglerFunction} [onClose] invoked after closing the accordion
 * @property {MyAccordionTogglerFunction} [onToggle] invoked after opening/closing the accordion
 */
const MyAccordion = class extends Component {
	static propTypes = {
		/**
		 * @desc The title to be displayed in accordion header
		 * @desc It can be a simple string, an HTML Element, a React Component, an array of them or a group of them wrapped by <>…</>
		 * @type {JSX.Element|React.node|string}
		 */
		title: PropTypes.oneOfType([PropTypes.node, PropTypes.string]).isRequired,
		/**
		 * @desc The bullet of the accordion toggler
		 * @desc It's automatically rotated, defaults to a triangle
		 * @desc The toggler is on display in a 22px squared block.
		 * @desc It's rotated 90° clockwise when accordion is open.
		 * @type {JSX.Element|React.node|string}
		 */
		bullet: PropTypes.oneOfType([PropTypes.node, PropTypes.string]),
		/**
		 * Initially open? Defaults to false
		 * @type {boolean}
		 */
		open: PropTypes.bool,
		/**
		 * Optional additional style properties for header
		 * @type {React.CSSProperties}
		 */
		headerStyle: PropTypes.object,
		/**
		 * A function to be invoked before opening the accordion
		 * @type {MyAccordionKnownTogglerFunction}
		 */
		onBeforeOpen: PropTypes.func,
		/**
		 * A function to be invoked before closing the accordion
		 * @type {MyAccordionKnownTogglerFunction}
		 */
		onBeforeClose: PropTypes.func,
		/**
		 * A function to be invoked after opening the accordion
		 * @type {MyAccordionKnownTogglerFunction}
		 */
		onOpen: PropTypes.func,
		/**
		 * A function to be invoked after closing the accordion
		 * @type {MyAccordionKnownTogglerFunction}
		 */
		onClose: PropTypes.func,
		/**
		 * A function to be invoked after opening/closing the accordion
		 * @type {MyAccordionTogglerFunction}
		 */
		onToggle: PropTypes.func,
		children: PropTypes.node,
	}
	/** @param { MyAccordionProps } props */
	constructor(props) {
		super(props);
		/** @type {MyAccordionState } */
		this.state = { open: !!props.open };
		this.toggle = this.toggle.bind(this);
		this.uid = `${baseClassName}-${Date.now()}-${String(Date.now() * Math.random()).replace(/[^0-9]/g, "")}`;
	}

	/**
	 * @function
	 * @private
	 * @param {boolean} mode Is the accordion to be opened?
	 * @return {void}
	 */
	toggle(mode) {
		if (typeof mode === 'undefined') mode = !this.state.open;
		const accordionElement = window.document.getElementById(this.uid);
		if ( mode && typeof this.props.onBeforeOpen === 'function' ) {
			this.props.onBeforeOpen( accordionElement );
		} else if ( ! mode && typeof this.props.onBeforeClose === 'function' ) {
			this.props.onBeforeClose( accordionElement );
		}
		this.setState({open: mode}, () =>{
			if ( mode && typeof this.props.onOpen === 'function' ) {
				this.props.onOpen( accordionElement );
			} else if ( ! mode && typeof this.props.onClose === 'function' ) {
				this.props.onClose( accordionElement );
			}
			if ( typeof this.props.onToggle === 'function' ) {
				this.props.onToggle( accordionElement, !! mode );
			}
		});
	}

	render() {
		return (
			<div
				className={baseClassName + ' ' + (this.state.open ? 'expanded' : 'collapsed')}
				id={this.uid}
			>
				<div
					className={baseClassName + 'Header'}
					style={ this.props.headerStyle || {}}
				>
					<div
						key={this.state.bullet}
						className={baseClassName + 'Toggler'}
						onClick={ ()=>{ this.toggle() } }
					>
						{ this.props.bullet ?? <MyBullet /> }
					</div>
					<div
						key={String(this.props.title)}
						className={baseClassName + 'Title'}
						onClick={ ()=>{ this.toggle() } }
					>
						{this.props.title}
					</div>
				</div>
				<div className={baseClassName + 'Body'}>
					<div className={baseClassName + 'InnerBody'}>
						{ this.props.children }
					</div>
				</div>
			</div>
		);
	}
}

export default MyAccordion;